import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BooleanDisplay, Button, Divider, Loading, Panel, Typography } from "@/components/atoms";
import { ConfigDetail } from "@/components/molecules/ConfigDetail/ConfigDetail";
import { useLazyGetOffloadingConfigurationQuery } from "@/redux/apis/addOn/addOnApi";
import { useAppSelector } from "@/redux/hooks";
import { addOnItemSelector } from "@/redux/slices/addOn/selectors";
import { OffloadingConfiguration } from "@/redux/slices/addOn/types";
import { OffloadingInfoIcon, renderDispatchTime } from "./helpers";

export const OffloadingDetailsInDispatch = () => {
  const navigate = useNavigate();
  const offloading = useAppSelector(addOnItemSelector("offloading"));
  const configuration = offloading?.configuration as OffloadingConfiguration | undefined;
  const [getOffloadingConfiguration, { isFetching }] = useLazyGetOffloadingConfigurationQuery();

  useEffect(() => {
    getOffloadingConfiguration();
  }, [getOffloadingConfiguration]);

  if (!offloading) return null;

  return (
    <Panel className="mt-5">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Typography variant="h3" className="leading-8">
              Offloading
            </Typography>
            <OffloadingInfoIcon />
          </div>
          <Typography className="text-neutral-dark-gray">
            Automatically dispatch jobs to your private or public network when needed.
          </Typography>
        </div>
        <div className="ml-auto flex gap-3">
          <Button startIcon="Edit2" onClick={() => navigate("edit/offloading")}>
            Edit
          </Button>
        </div>
      </div>

      <div className="relative my-4 flex w-full flex-col rounded-lg bg-neutral-surface-gray p-5">
        {isFetching && <Loading className="rounded-lg" />}
        <div className="flex flex-row items-center">
          <div className="flex flex-1 flex-col">
            <Typography variant="action">Auto Dispatch to Private Network</Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              Set the time before pickup
            </Typography>
          </div>
          <BooleanDisplay status={configuration?.enabledAutomaticDispatchPrivateNetwork}>
            <Typography variant="paragraph">{configuration?.enabledAutomaticDispatchPrivateNetwork ? "Enabled" : "Disabled"}</Typography>
          </BooleanDisplay>
        </div>
        <ConfigDetail title="Dispatch Tier Times" description="Timing of when to dispatch to each tier." indentPadding="pl-2" />
        <ConfigDetail title="Tier 1" description="Set when the system will start dispatching before pickup" indentPadding="pl-4" isBullet>
          {renderDispatchTime(configuration?.dispatchPrivateNetworkTier1Time, configuration?.dispatchPrivateNetworkTier1Timeframe)}
        </ConfigDetail>
        <ConfigDetail title="Tier 2" description="Set when the system will start dispatching before pickup" indentPadding="pl-4" isBullet>
          {renderDispatchTime(configuration?.dispatchPrivateNetworkTier2Time, configuration?.dispatchPrivateNetworkTier2Timeframe)}
        </ConfigDetail>
        <ConfigDetail title="Tier 3" description="Set when the system will start dispatching before pickup" indentPadding="pl-4" isBullet>
          {renderDispatchTime(configuration?.dispatchPrivateNetworkTier3Time, configuration?.dispatchPrivateNetworkTier3Timeframe)}
        </ConfigDetail>
        <Divider className="my-6" />
        <div className="flex flex-row items-center">
          <div className="flex flex-1 flex-col">
            <Typography variant="action">Auto Dispatch to Public Network</Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              Set the time before pickup
            </Typography>
          </div>
          <BooleanDisplay status={configuration?.enabledAutomaticDispatchPublicNetwork}>
            <Typography variant="paragraph">{configuration?.enabledAutomaticDispatchPublicNetwork ? "Enabled" : "Disabled"}</Typography>
          </BooleanDisplay>
        </div>
        <ConfigDetail title="" description="Set when the system will start dispatching before pickup" indentPadding="pl-2">
          <Typography variant="paragraph">
            {renderDispatchTime(configuration?.dispatchAutoPublicNetworkTime, configuration?.dispatchAutoPublicNetworkTimeframe)}
          </Typography>
        </ConfigDetail>
      </div>
    </Panel>
  );
};
